/* Câu hỏi thường gặp — /cau-hoi-thuong-gap
 *
 * Content comes from two places on purpose (see SiteInertiaController::faq): the `Page` row
 * supplies title/SEO/intro, the questions are `faq.element` frontend rows edited at
 * /admin/frontend/sections/faq. The controller sends the questions flat; the category chips
 * and the search are derived here so the filter has one implementation.
 *
 * Contrast note: WebLayoutTemplate paints <main> #F4F5F9, a tinted ground. Per the rules in
 * app.css, secondary text on it must use --muted-foreground-strong (plain --muted-foreground
 * is only 4.34:1 there) and brand-coloured text/focus rings use --primary-ink; --primary
 * itself is ~3.1:1 on white and is fill-only.
 */
import SiteController from '@/actions/App/Http/Controllers/SiteController';
import SiteInertiaController from '@/actions/App/Http/Controllers/SiteInertiaController';
import { RichContentRenderer } from '@/components/seo-content-renderer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Typo } from '@/components/ui/typography';
import { WebBreadcrumb } from '@/components/web/layouts/web-breadcrumb';
import WebLayout from '@/components/web/layouts/web-layout';
import { generateSlug } from '@/lib/slug';
import { cn } from '@/lib/utils';
import { type BreadcrumbItem } from '@/types';
import { type SEOContent } from '@/types/page-builder';
import { Head, usePage } from '@inertiajs/react';
import { Mail, MapPin, MessageCircleQuestion, Phone, Search } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';

interface FaqItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

interface FaqPageProps {
  pageTitle: string;
  heading: string;
  subtitle: string | null;
  intro: string | null;
  faqs: FaqItem[];
  seoContents: SEOContent | null;
  seoImage: string | null;
}

/** Shape of the `pageInfo` shared prop — see components/web/home/newsletter-section.tsx. */
interface PageInfoItem {
  type?: string;
  value?: string;
}

const ALL_CATEGORIES = 'Tất cả chủ đề';

const BREADCRUMBS: BreadcrumbItem[] = [{ title: 'Câu hỏi thường gặp' }];

/**
 * Diacritic- and case-insensitive matching by running both sides through the same slug
 * transform, so "ve dien tu" finds "Vé điện tử".
 */
const fold = (value: string) => generateSlug(value);

const CHIP_CLASSES =
  'focus-visible:outline-primary-ink shrink-0 rounded-chip border px-4 py-2 text-sm font-medium transition-colors duration-200 cursor-pointer ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 motion-reduce:transition-none';

export default function Faq({ pageTitle, heading, subtitle, intro, faqs, seoContents, seoImage }: FaqPageProps) {
  const page = usePage();
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORIES);

  const seoDescription =
    seoContents?.description || subtitle || 'Giải đáp các thắc mắc thường gặp về đặt vé, thanh toán, vé điện tử và chính sách hoàn hủy.';
  const canonical = SiteInertiaController.faq().url;

  const categories = useMemo(() => [ALL_CATEGORIES, ...new Set(faqs.map((faq) => faq.category))], [faqs]);

  const visibleFaqs = useMemo(() => {
    const needle = fold(query);

    return faqs.filter((faq) => {
      const matchesCategory = activeCategory === ALL_CATEGORIES || faq.category === activeCategory;
      const matchesQuery = !needle || fold(`${faq.question} ${faq.answer}`).includes(needle);

      return matchesCategory && matchesQuery;
    });
  }, [faqs, query, activeCategory]);

  const [address, phone, email] = useMemo(() => {
    // getPageInfo() deserialises to an object rather than a list whenever the stored
    // page_info has non-sequential keys, and it is not shared outside `web.inertia` at all.
    const items: PageInfoItem[] = Array.isArray(page.props.pageInfo) ? (page.props.pageInfo as PageInfoItem[]) : [];

    return [
      items.find((item) => item.type === 'address')?.value,
      items.find((item) => item.type === 'phone')?.value,
      items.find((item) => item.type === 'email')?.value,
    ];
  }, [page.props.pageInfo]);

  return (
    <WebLayout>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoContents?.social_title || pageTitle} />
        <meta property="og:description" content={seoContents?.social_description || seoDescription} />
        <meta property="og:type" content="website" />
        {seoImage && <meta property="og:image" content={seoImage} />}
        <link rel="canonical" href={canonical} />

        {/* Every question, not the filtered subset — the schema describes the document,
            not the current UI state. */}
        {faqs.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            })}
          </script>
        )}
      </Head>

      <section className="from-primary/10 bg-gradient-to-r to-purple-500/10 py-10 sm:py-14">
        <div className="container mx-auto max-w-3xl px-4">
          <WebBreadcrumb items={BREADCRUMBS} className="mb-6" />

          {/* text-left overrides the h1 variant's default text-center — the rest of the
              hero (breadcrumb, subtitle, search) is left-aligned. */}
          <Typo variant="h1" className="text-left text-3xl font-bold tracking-tight sm:text-4xl">
            {heading}
          </Typo>
          {subtitle && (
            <Typo variant="muted" className="text-muted-foreground-strong mt-3">
              {subtitle}
            </Typo>
          )}

          <div className="relative mt-7">
            <Search className="text-muted-foreground-strong pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2" aria-hidden="true" />
            <Input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Tìm kiếm câu hỏi…"
              aria-label="Tìm kiếm câu hỏi"
              className="focus-visible:ring-primary-ink rounded-chip border-pill-border h-12 bg-white pl-11 text-base"
            />
          </div>
        </div>
      </section>

      <div className="container mx-auto max-w-3xl px-4 py-10 sm:py-12">
        {intro && <RichContentRenderer editorSerializedState={intro} className="prose prose-slate mb-10 max-w-none" />}

        {categories.length > 2 && (
          <div className="-mx-4 mb-8 flex gap-2 overflow-x-auto px-4 pb-2 horizontal-scrollbar" role="group" aria-label="Lọc theo chủ đề">
            {categories.map((category) => {
              const isActive = category === activeCategory;

              return (
                <button
                  key={category}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setActiveCategory(category)}
                  className={cn(
                    CHIP_CLASSES,
                    isActive
                      ? 'border-primary bg-primary text-white'
                      : 'border-pill-border bg-pill text-muted-foreground-strong hover:border-primary hover:text-primary-ink',
                  )}
                >
                  {category}
                </button>
              );
            })}
          </div>
        )}

        {visibleFaqs.length > 0 ? (
          <Accordion type="single" collapsible className="rounded-card border-pill-border shadow-card border bg-white px-5">
            {visibleFaqs.map((faq) => (
              <AccordionItem key={faq.id} value={String(faq.id)} className="border-pill-border last:border-b-0">
                <AccordionTrigger className="focus-visible:outline-primary-ink gap-4 text-left text-base font-semibold hover:no-underline focus-visible:outline-2 focus-visible:outline-offset-2">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground-strong text-base leading-7 whitespace-pre-line">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <div className="rounded-card border-pill-border flex flex-col items-center border border-dashed bg-white px-6 py-14 text-center">
            <MessageCircleQuestion className="text-primary-ink size-8" aria-hidden="true" />
            <Typo variant="h3" className="mt-4 text-lg font-semibold">
              {faqs.length === 0 ? 'Nội dung đang được cập nhật' : 'Không tìm thấy câu hỏi phù hợp'}
            </Typo>
            <Typo variant="muted" className="text-muted-foreground-strong mt-2 max-w-md">
              {faqs.length === 0
                ? 'Danh sách câu hỏi thường gặp sẽ sớm được bổ sung. Trong lúc chờ, bạn có thể liên hệ trực tiếp với chúng tôi.'
                : 'Thử một từ khóa khác, hoặc liên hệ với chúng tôi để được hỗ trợ trực tiếp.'}
            </Typo>
          </div>
        )}

        {/* Chưa tìm thấy câu trả lời? */}
        <div className="rounded-card border-pill-border shadow-card mt-10 border bg-white p-6 sm:p-8">
          <Typo variant="h2" className="text-xl font-bold">
            Chưa tìm thấy câu trả lời?
          </Typo>
          <Typo variant="muted" className="text-muted-foreground-strong mt-2">
            Đội ngũ hỗ trợ của chúng tôi luôn sẵn sàng giải đáp thắc mắc của bạn.
          </Typo>

          <dl className="mt-6 grid gap-4 sm:grid-cols-2">
            {phone && (
              <ContactRow
                icon={<Phone className="size-4" aria-hidden="true" />}
                label="Hotline"
                value={phone}
                href={`tel:${phone.replace(/\s+/g, '')}`}
              />
            )}
            {email && <ContactRow icon={<Mail className="size-4" aria-hidden="true" />} label="Email" value={email} href={`mailto:${email}`} />}
            {address && <ContactRow icon={<MapPin className="size-4" aria-hidden="true" />} label="Địa chỉ" value={address} />}
          </dl>

          {/* Plain anchor: /contact is a Blade route on a different bundle, so an Inertia
              visit would resolve against the wrong root view. */}
          <a
            href={SiteController.contact().url}
            className="bg-primary hover:bg-brand-orange-hover focus-visible:outline-primary-ink rounded-chip mt-6 inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 motion-reduce:transition-none"
          >
            Gửi yêu cầu hỗ trợ
          </a>
        </div>
      </div>
    </WebLayout>
  );
}

function ContactRow({ icon, label, value, href }: { icon: ReactNode; label: string; value: string; href?: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-primary-ink mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0">
        <dt className="text-muted-foreground-strong text-xs font-semibold tracking-[0.12em] uppercase">{label}</dt>
        <dd className="text-foreground mt-0.5 font-medium break-words">
          {href ? (
            <a
              href={href}
              className="focus-visible:outline-primary-ink rounded-sm hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              {value}
            </a>
          ) : (
            value
          )}
        </dd>
      </div>
    </div>
  );
}
